/** The Time Management API as React Query hooks. Holidays are primed into pages/time/calendarDays.ts so synchronous date helpers can read them without a prop. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import { primeHolidays, type Holiday, type HolidayKind } from '../pages/time/calendarDays'
import type { ApproverBalance } from '../pages/time/approverBalance'
import type { ResetCorrectionPayload } from '../pages/time/resetCorrection'
import { api } from './api'
import { dayActionBody } from './statusOverride'
import { toLineInput } from './timeLineInput'
import { withNumericHours } from './timeWire'


export interface TimePerson {
  id: string
  name: string
  position: string
  team: string
  email: string
  supervisor_id: string | null
  is_self: boolean
  /** `staff_info.work_type`; only 'Full Time' leave skips a public holiday it spans. */
  work_type: string
  /** Whether the person has a roster_info row; optional, an unknown must never hide a mode. */
  on_roster?: boolean
  /** Whether the person takes leave; optional, an unknown keeps them on the leave team list. */
  takes_leave?: boolean
}

/** The /api/time/people envelope: `total` and `truncated` keep a short list from reading as a smaller company. */
interface TimeRoster {
  people: TimePerson[]
  total: number
  truncated: boolean
}

export interface ApiLogLine {
  id?: string
  market: string
  activity_id: string
  /** Display name; read-only, the id identifies. */
  activity?: string
  product_service_id: string | null
  /** Resolved server-side so a viewer without the project still sees a name. Read-only. */
  product_service_title?: string
  shift: '' | 'Day' | 'Night'
  /** Stored per line, shown per day; '' means the source recorded no mode. */
  work_mode?: string
  hours: number
  overtime_hours: number
  nighttime_hours: number
  over_night_hours: number
  transit_hours: number
  comment: string
}

export interface ApiDay {
  id: string
  employee_id: string
  day: string
  kind: 'Plan' | 'Actual'
  mode: string
  status: 'Draft' | 'Pending' | 'Approved' | 'Rejected'
  rejection_reason: string
  // Blank when nobody has rejected it; render conditionally.
  rejected_by: string
  // Optional; absent reads as nobody.
  rejected_by_id?: string | null
  // A name, the sentinel BEFORE_TRACKING, or blank; read through daySignature, never print it raw.
  decided_by: string
  // Raw ISO 8601 or null; the client decides how a moment reads.
  decided_at: string | null
  // 'human' | 'system' | ''; optional on the type so older fixtures compile, a missing value reads as ''.
  decided_actor_kind?: string
  // Decimal hours as strings; null on every unclocked day (the common case), so render empty, never a default.
  start_of: string | null
  end_of: string | null
  gap: string | null
  extra_hours: string | null
  // Derived by the server, never accepted from a client.
  total: string | null
  // Payroll's weighted figure for the day, priced by the server; null or absent when it priced none.
  weighted_total?: string | null
  // ⛔ Blank means no plan was filed, not unknown; required so a missing value cannot read as blank.
  plan_status: string
  plan_mode: string
  lines: ApiLogLine[]
  // Null cosign_requested_at is an ordinary day and the other four mean nothing then; optional, absent reads as never requested.
  cosign_requested_at?: string | null
  cosign_supervisor?: CosignVerdict
  cosign_ceo?: CosignVerdict
  cosign_cfo?: CosignVerdict
  cosign_closed_at?: string | null
}

/** Waived: written by the server for a supervisor line that is never signed. */
export type CosignVerdict = 'Pending' | 'Approved' | 'Rejected' | 'Waived'
export type CosignStage = 'supervisor' | 'ceo' | 'cfo'

/** `signable` is the server's answer; never re-derive it. */
export interface ApiCosignRequest extends ApiDay {
  employee_name: string
  signable: CosignStage[]
}

export { ALREADY_APPROVED, COSIGN_REQUIRED } from './timeCodes'

/** The four numbers a client may write; total is derived server-side and cannot be sent. */
export interface DayClock {
  start_of: number
  end_of: number
  gap: number
  extra_hours: number
}

export interface ApiLeave {
  id: string
  employee_id: string
  leave_type: string
  start_date: string
  end_date: string
  half_day: boolean
  days: number
  reason: string
  status: string
  requested_date: string | null
  approved_date: string | null
  supervisor_status: string
  management_status: string
  finance_status: string
  comment: string
  // Software-written explanation, never a person's words; render as muted machine text apart from `comment`.
  system_reason: string
  // Blanked with comment and system_reason on somebody else's request.
  rejected_by: string
  attachments: number
  // Null means nobody was named and the HR-peer fallback applies at approval.
  stand_in_id: string | null
  // Computed per caller: set only for the stand-in currently covering the owner.
  on_behalf_of: { id: string; name: string } | null
  /** The server's verdict that a stage this caller signs is open, which covers standing in for the away CFO. */
  awaits_me?: boolean
}

export interface ApiActivity {
  id: string
  title: string
  description_en: string
  description_mn: string
  is_transit: boolean
  is_downtime: boolean
  is_active: boolean
  sort_order: number
}

export interface ApiProduct {
  id: string
  title: string
  type_of: string
  shift_type?: string
  is_active: boolean
  earns_transit_allowance: boolean
  /** Most a downtime day pays on this project (Decimal as text); set only by the typical-shift-hours PATCH. */
  typical_shift_hours?: number | string
}

export interface ApiBalance {
  employee_id: string
  name: string
  al_days: number
  cl_days: number
  psl_days: number
  spl_days: number
  taken_sl_days: number
  /** Days earned over a trailing window, not a granted balance; arrives as a string, so read via Number(...). */
  rl_days: number
  /** What the latest year-end reset recorded as lapsed, read off its own row. */
  cl_lapsed_days?: number | string
  psl_lapsed_days?: number | string
  /** The year that reset closed; null when none has run for them. */
  reset_year?: number | null
}

export interface ApiDayWindow {
  today: string
  self_start: string
  open_window_days: number
  may_backdate: boolean
  /** The date a save is refused before for this caller; optional so older fixtures compile. */
  backdate_floor?: string
  /** Earliest date a late day may be sent for co-signature; optional, absent reads as unknown. */
  cosign_floor?: string | null
  /** Absent reads as three signers. */
  cosign_supervisor_waived?: boolean
  /** false means co-signature is paused; absent reads as unknown (no sentence, no button). */
  cosign_requests_enabled?: boolean
  /** Last day the pay-date lock closes against this caller, or null. Required: a missing value would fail open.
   *  Not part of `dayIsOpen`: the server refuses it separately, for Actuals only. */
  actuals_locked_through: string | null
  /** The calendar's last pay date once today is past it: the lock then has no pay date to name. */
  pay_calendar_ended_on?: string | null
  /** Closed payroll months as date ranges (a payroll month is not a calendar month); required because it gates a button. */
  closed_months: ApiClosedMonth[]
  /** Required: the editor's Save stays disabled until these are known. */
  limits: DayLimits
}

/** The server's rule constants. */
export interface DayLimits {
  downtime_cap_from: string
  default_typical_shift_hours: number
  max_day_hours: number
  max_short_transit_day_hours: number
  long_transit_over_hours: number
  max_normal_hours: number
  max_transit_hours: number
  max_night_hours: number
  max_overtime_hours: number
  max_downtime_hours: number
  full_balance_triggers: string[]
  gross_only_through: { year: number; month: number }
}

type ApiClosedMonth = { year: number; month: number; start: string; end: string }

/** One pay period as dates only: the server ships exactly these fields so no amount can leak in. */
export interface ApiPayCalendarEntry {
  year: number
  month: number
  run: string
  cut_off: string
  pay_date: string
}

export const timeKeys = {
  payCalendar: ['time', 'pay-calendar'] as const,
  payPeriods: ['payroll', 'periods'] as const,
  people: ['time', 'people'] as const,
  dayWindow: ['time', 'day-window'] as const,
  calendar: (from: string, to: string) => ['time', 'calendar', from, to] as const,
  calendarCoverage: ['time', 'calendar', 'coverage'] as const,
  activities: ['time', 'activities'] as const,
  products: (mine: boolean) => ['time', 'products', mine] as const,
  days: (employeeId: string, from: string, to: string) =>
    ['time', 'days', employeeId, from, to] as const,
  leave: (scope: string, from?: string, to?: string) =>
    from && to
      ? (['time', 'leave', scope, from, to] as const)
      : (['time', 'leave', scope] as const),
  balances: ['time', 'balances'] as const,
  cosign: ['time', 'cosign'] as const,
  cosignRefused: ['time', 'cosign', 'refused'] as const,
}


/** Short staleTime: `today` rolls at midnight in the business timezone and `self_start` moves on a cut-off. */
export function useDayWindow(enabled = true) {
  return useQuery({
    queryKey: timeKeys.dayWindow,
    queryFn: async () => (await api.get<ApiDayWindow>('/api/time/day-window')).data,
    staleTime: 60_000,
    enabled,
  })
}

/** The whole active roster, not a page: callers look people up in it. `truncated` is passed through. */
/** Pay calendar. Still gated: a caller without access gets a 403, so render on `data`, never on the absence of an error.
 *  Long staleTime. */
export function usePayCalendar() {
  return useQuery({
    queryKey: timeKeys.payCalendar,
    queryFn: async () =>
      (await api.get<ApiPayCalendarEntry[]>('/api/pay-calendar/periods')).data,
    staleTime: 10 * 60_000,
  })
}

export function usePeople() {
  return useQuery({
    queryKey: timeKeys.people,
    queryFn: async () => (await api.get<TimeRoster>('/api/time/people')).data,
    staleTime: 5 * 60_000,
  })
}


/** Fetch a wide range once; the company calendar is a few dozen rows a year. */
export function useHolidays(from: string, to: string) {
  const query = useQuery({
    queryKey: timeKeys.calendar(from, to),
    queryFn: async () =>
      (await api.get<Holiday[]>('/api/time/calendar', { params: { from, to } })).data,
    staleTime: 10 * 60_000,
  })
  useEffect(() => {
    if (query.data) primeHolidays(query.data)
  }, [query.data])
  return query
}

export interface LeaveChoice {
  leave_id: string
  choice: string
}

export interface HolidayLeaveOption {
  choice: string
  start_date: string
  end_date: string
  days: string
  cancels: boolean
  refused: string | null
}

export interface HolidayLeaveImpact {
  leave_id: string
  person_name: string
  leave_type: string
  status: string
  start_date: string
  end_date: string
  days: string
  refusal: string | null
  stuck: boolean
  options: HolidayLeaveOption[]
}

export async function fetchHolidayLeaveImpact(day: string, adding: boolean): Promise<HolidayLeaveImpact[]> {
  return (
    await api.get<HolidayLeaveImpact[]>(`/api/time/calendar/${day}/leave-impact`, { params: { adding } })
  ).data
}

export function useSetHoliday() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { day: string; kind: HolidayKind; title: string; leaveChoices?: LeaveChoice[] }) =>
      (
        await api.put<Holiday>(`/api/time/calendar/${input.day}`, {
          holiday_kind: input.kind,
          title: input.title,
          leave_choices: input.leaveChoices ?? [],
        })
      ).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['time', 'calendar'] }),
  })
}

export function useClearHoliday() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { day: string; leaveChoices?: LeaveChoice[] }) => {
      await api.delete(`/api/time/calendar/${input.day}`, {
        data: { leave_choices: input.leaveChoices ?? [] },
      })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['time', 'calendar'] }),
  })
}

/** A year absent here is unconfirmed, not holiday-free. */
export function useCalendarCoverage() {
  return useQuery({
    queryKey: timeKeys.calendarCoverage,
    queryFn: async () => (await api.get<number[]>('/api/time/calendar/coverage')).data,
    staleTime: 60_000,
  })
}

export function useConfirmCalendarYear() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (year: number) => {
      await api.put(`/api/time/calendar/years/${year}`)
    },
    onSuccess: () =>
      Promise.all(
        [timeKeys.calendarCoverage, timeKeys.payPeriods, timeKeys.dayWindow].map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      ),
  })
}

export interface CalendarCopyResult {
  year: number
  source_year: number
  copied: Holiday[]
  skipped: string[]
}

/** Start an empty, unconfirmed year as last year's holidays; the year stays unconfirmed. */
export function useCopyCalendarYear() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (year: number) =>
      (await api.post<CalendarCopyResult>(`/api/time/calendar/years/${year}/copy`)).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['time', 'calendar'] })
      void queryClient.invalidateQueries({ queryKey: timeKeys.calendarCoverage })
    },
  })
}


export function useActivities() {
  return useQuery({
    queryKey: timeKeys.activities,
    queryFn: async () => (await api.get<ApiActivity[]>('/api/time/activities')).data,
    staleTime: 5 * 60_000,
  })
}

/** `mine` is the products the person holds a role on, plus Trigteq. */
export function useProducts(mine = true) {
  return useQuery({
    queryKey: timeKeys.products(mine),
    queryFn: async () =>
      (await api.get<ApiProduct[]>('/api/time/products', { params: { mine } })).data,
    staleTime: 5 * 60_000,
  })
}

export function useAllProducts() {
  return useQuery({
    queryKey: ['hr', 'products', 'all'],
    queryFn: async () =>
      (await api.get<ApiProduct[]>('/api/hr/products', { params: { include_inactive: true } })).data,
  })
}

export function useSaveProduct() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id?: string; payload: Partial<ApiProduct> }) =>
      id
        ? (await api.patch<ApiProduct>(`/api/hr/products/${id}`, payload)).data
        : (await api.post<ApiProduct>('/api/hr/products', payload)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'products'] })
      queryClient.invalidateQueries({ queryKey: ['time', 'products'] })
      queryClient.invalidateQueries({ queryKey: ['roster', 'projects'] })
    },
  })
}

export function useSaveActivity() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id?: string; payload: Partial<ApiActivity> }) =>
      id
        ? (await api.patch<ApiActivity>(`/api/time/activities/${id}`, payload)).data
        : (await api.post<ApiActivity>('/api/time/activities', payload)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: timeKeys.activities }),
  })
}

type ActivityMerge = {
  activity: string
  reassign_to: string
  lines: number
  reassigned: number
  merged: number
  days: number
}

/** Counts from the server that will do the work; disabled until a target is picked. */
export function useActivityUsage(id: string | null, reassignTo: string) {
  return useQuery({
    queryKey: ['time', 'activities', id, 'usage', reassignTo],
    enabled: id != null && reassignTo !== '',
    queryFn: async () =>
      (
        await api.get<ActivityMerge>(`/api/time/activities/${id}/usage`, {
          params: { reassign_to: reassignTo },
        })
      ).data,
  })
}

/** Invalidates the day logs too: payroll reads the same lines. */
export function useDeleteActivity() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, reassignTo }: { id: string; reassignTo: string }) =>
      (
        await api.delete<ActivityMerge>(`/api/time/activities/${id}`, {
          params: { reassign_to: reassignTo },
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeKeys.activities })
      queryClient.invalidateQueries({ queryKey: ['time', 'days'] })
      queryClient.invalidateQueries({ queryKey: ['payroll'] })
    },
  })
}


export function useDays(employeeId: string | undefined, from: string, to: string, allowed = true) {
  return useQuery({
    queryKey: timeKeys.days(employeeId ?? 'me', from, to),
    queryFn: async () =>
      (
        await api.get<ApiDay[]>('/api/time/days', {
          params: { from, to, ...(employeeId ? { employee_id: employeeId } : {}) },
        })
      ).data.map(withNumericHours),
    enabled: allowed && Boolean(from && to),
  })
}

export function useSaveDay() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      employeeId: string
      day: string
      kind: 'Plan' | 'Actual'
      mode: string
      // `null` erases the clock; a save is the whole day.
      clock: DayClock | null
      lines: ApiLogLine[]
    }) =>
      withNumericHours((
        await api.put<ApiDay>(
          `/api/time/days/${input.employeeId}/${input.day}/${input.kind}`,
          { mode: input.mode, clock: input.clock, lines: input.lines.map(toLineInput) },
        )
      ).data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['time', 'days'] }),
  })
}

/** Erases the day row; invalidates the same ['time', 'days'] key as the writes. */
export function useDeleteDay() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { employeeId: string; day: string; kind: 'Plan' | 'Actual' }) =>
      (
        await api.delete<void>(
          `/api/time/days/${input.employeeId}/${input.day}/${input.kind}`,
        )
      ).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['time', 'days'] }),
  })
}

/** All-or-nothing on the server: a closed day fails the whole batch. */
export function useSaveDaysBatch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      employeeId: string
      dates: string[]
      mode: string
      // One clock across the whole selection.
      clock: DayClock | null
      lines: ApiLogLine[]
    }) =>
      (
        await api.put<ApiDay[]>(`/api/time/days/${input.employeeId}/plan-batch`, {
          dates: input.dates,
          mode: input.mode,
          clock: input.clock,
          lines: input.lines.map(toLineInput),
        })
      ).data.map(withNumericHours),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['time', 'days'] }),
  })
}

export function useDayAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      dayId: string
      action: 'submit' | 'decision' | 'status'
      verdict?: 'Approved' | 'Rejected'
      comment?: string
      confirmReapproval?: boolean
      expectedStatus?: string
    }) =>
      withNumericHours((
        await api.post<ApiDay>(
          `/api/time/days/${input.dayId}/${input.action}`,
          dayActionBody(input),
          input.confirmReapproval ? { params: { confirm_reapproval: true } } : undefined,
        )
      ).data),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['time', 'days'] }),
  })
}

/** Per-day outcomes: a refusal is a normal 200 with `decided: false`, never a thrown error. */
interface ApiBatchDecisionReport {
  verdict: string
  decided: number
  refused: number
  results: { day_id: string; day: string | null; decided: boolean; detail: string | null }[]
}

export function useDecideDays() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { day_ids: string[]; verdict: 'Approved' | 'Rejected'; comment: string }) =>
      (await api.post<ApiBatchDecisionReport>('/api/time/days/decisions', body)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['time'] }),
  })
}


export function useCosignQueue() {
  return useQuery({
    queryKey: timeKeys.cosign,
    queryFn: async () => (await api.get<ApiCosignRequest[]>('/api/time/cosign')).data.map(withNumericHours),
    staleTime: 30_000,
  })
}

/** The one path a late Actual takes; the day comes back Pending with all three lines open. */
export function useRequestCosign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      employeeId: string
      day: string
      mode: string
      clock: DayClock | null
      lines: ApiLogLine[]
    }) =>
      withNumericHours((
        await api.put<ApiDay>(`/api/time/cosign/${input.employeeId}/${input.day}`, {
          mode: input.mode,
          clock: input.clock,
          lines: input.lines.map(toLineInput),
        })
      ).data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['time', 'days'] })
      void queryClient.invalidateQueries({ queryKey: timeKeys.cosign })
    },
  })
}

export function useSignCosign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      dayId: string
      stage: CosignStage
      verdict: 'Approved' | 'Rejected'
      comment?: string
    }) =>
      withNumericHours((
        await api.post<ApiDay>(`/api/time/cosign/${input.dayId}/sign`, {
          stage: input.stage,
          verdict: input.verdict,
          comment: input.comment ?? '',
        })
      ).data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['time', 'days'] })
      void queryClient.invalidateQueries({ queryKey: timeKeys.cosign })
    },
  })
}

/** Refusals the caller may still take back; `signable` is always empty. */
export function useCosignRefusals() {
  return useQuery({
    queryKey: timeKeys.cosignRefused,
    queryFn: async () =>
      (await api.get<ApiCosignRequest[]>('/api/time/cosign', { params: { view: 'refused' } })).data.map(withNumericHours),
    staleTime: 30_000,
  })
}

export function useReverseCosign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { dayId: string }) =>
      withNumericHours((await api.post<ApiDay>(`/api/time/cosign/${input.dayId}/reverse`)).data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['time', 'days'] })
      void queryClient.invalidateQueries({ queryKey: timeKeys.cosign })
    },
  })
}


/** `range` selects requests overlapping from..to, not a start_date window. */
export function useLeave(scope: 'own' | 'team' | 'all', range?: { from: string; to: string }) {
  return useQuery({
    queryKey: timeKeys.leave(scope, range?.from, range?.to),
    queryFn: async () =>
      (
        await api.get<ApiLeave[]>('/api/time/leave', {
          params: { scope, ...(range ? { from: range.from, to: range.to, limit: 200 } : {}) },
        })
      ).data,
  })
}

export function useRaiseLeave() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      leave_type: string
      start_date: string
      end_date: string
      half_day: boolean
      reason: string
      // Omitted: the server falls back to the applicant's HR peer.
      stand_in_id?: string
    }) => (await api.post<ApiLeave>('/api/time/leave', payload)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['time', 'leave'] }),
  })
}

export function useLeaveAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      id: string
      action: 'decision' | 'cancel'
      verdict?: 'Approved' | 'Rejected'
      comment?: string
      override_reason?: string
    }) =>
      (
        await api.post<ApiLeave>(
          `/api/time/leave/${input.id}/${input.action}`,
          input.action === 'decision'
            ? {
                verdict: input.verdict,
                comment: input.comment ?? '',
                ...(input.override_reason ? { override_reason: input.override_reason } : {}),
              }
            : undefined,
        )
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time', 'leave'] })
      queryClient.invalidateQueries({ queryKey: timeKeys.balances })
    },
  })
}

export function useEditLeave() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      (await api.patch<ApiLeave>(`/api/time/leave/${id}`, payload)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['time', 'leave'] }),
  })
}


export function useApproverBalance(employeeId: string) {
  return useQuery({
    queryKey: ['time', 'leave', 'balance', employeeId],
    queryFn: async () => (await api.get<ApproverBalance>(`/api/time/leave/balance/${employeeId}`)).data,
    retry: false,
  })
}

export function useBalances() {
  return useQuery({
    queryKey: timeKeys.balances,
    queryFn: async () => (await api.get<ApiBalance[]>('/api/time/balances')).data,
  })
}

export function useCorrectReset() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      employeeId,
      year,
      payload,
    }: {
      employeeId: string
      year: number
      payload: ResetCorrectionPayload
    }) => (await api.put(`/api/time/balances/${employeeId}/reset/${year}`, payload)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: timeKeys.balances }),
  })
}

export function useSetBalance() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      employeeId,
      payload,
    }: {
      employeeId: string
      // rl_days is omitted: it is computed on read and BalanceIn forbids it (422).
      payload: Partial<
        Omit<ApiBalance, 'employee_id' | 'name' | 'rl_days' | 'cl_lapsed_days' | 'psl_lapsed_days' | 'reset_year'>
      >
    }) => (await api.patch<ApiBalance>(`/api/time/balances/${employeeId}`, payload)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: timeKeys.balances }),
  })
}
